/**
 * wishlist-migration.js
 *
 * ゲスト（localStorage）→ ログイン（Customer metafield）
 * Wishlist引き継ぎ処理（v1）
 *
 * 実行タイミング：
 *   - /pages/wishlist の初回表示時
 *   - /account (マイページ) の初回表示時
 *
 * 依存：wishlist.js が先に読み込まれていること（WishlistDebug を利用）
 */

(() => {
  'use strict';

  const GUEST_STORAGE_KEY  = 'wishlist:v1';
  const MIGRATED_FLAG_KEY  = 'wishlist:migrated';   // 引き継ぎ済みフラグ（localStorage）
  const SCHEMA_VERSION     = 1;

  // ---- 設定読み取り（theme.liquid の data-wishlist-sync と同じブロックを参照）----
  function getSyncConfig() {
    const el = document.querySelector('script[data-wishlist-sync]');
    if (!el) return { enabled: false };
    try {
      const cfg = JSON.parse(el.textContent);
      if (!cfg || typeof cfg !== 'object') return { enabled: false };
      return {
        enabled:      Boolean(cfg.enabled),
        customerId:   cfg.customerId ?? null,
        appProxyPath: typeof cfg.appProxyPath === 'string' ? cfg.appProxyPath : '',
      };
    } catch {
      return { enabled: false };
    }
  }

  // ---- localStorage の安全な読み書き ----
  function safeRead(key) {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  function safeWrite(key, value) {
    try {
      localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
      return true;
    } catch {
      return false;
    }
  }

  function safeRemove(key) {
    try { localStorage.removeItem(key); } catch {}
  }

  // ---- ゲスト側データの正規化・バリデーション ----
  function normalizeGuestItems(raw) {
    if (!raw || typeof raw !== 'object') return [];
    if (!Array.isArray(raw.items)) return [];

    const result = [];
    const seen = new Set();

    for (const it of raw.items) {
      const variantId = Number(it?.variant_id);
      const productId = Number(it?.product_id);

      if (!Number.isFinite(variantId) || variantId <= 0) {
        console.warn('[wishlist-migration] invalid item skipped:', it);
        continue;
      }
      if (seen.has(variantId)) continue;
      seen.add(variantId);

      result.push({
        variant_id: variantId,
        product_id: Number.isFinite(productId) && productId > 0 ? productId : null,
        added_at:   typeof it?.added_at === 'string' ? it.added_at : null,
        handle:     typeof it?.handle   === 'string' ? it.handle   : null,
      });
    }

    return result;
  }

  // ---- サーバー側の現行データを取得 ----
  // サーバー仕様: POST, action="get", レスポンス { status: "succeeded" }
  // get では items を直接返さないため、マージはサーバー側（merge action）に委ねる
  async function fetchServerWishlist(appProxyPath) {
    const res = await fetch(appProxyPath, {
      method:      'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept:         'application/json',
      },
      body: JSON.stringify({ action: 'get' }),
    });

    if (!res.ok) throw new Error(`fetch server wishlist failed: ${res.status}`);

    const data = await res.json();
    if (data?.status !== 'succeeded') {
      throw new Error(data?.message || 'server returned non-succeeded status');
    }

    // サーバーは get で items を直接返さないので空を返す
    // 実際のマージはサーバー側 merge action が行う
    return { version: SCHEMA_VERSION, updated_at: null, items: [] };
  }

  // ---- マージ処理（仕様 §4 の和集合・重複排除）----
  function mergeItems(serverItems, guestItems) {
    // サーバー側を正とし、Map に展開（variantId → item）
    const merged = new Map();

    for (const it of serverItems) {
      const vid = Number(it?.variant_id);
      if (!Number.isFinite(vid) || vid <= 0) continue;
      merged.set(vid, {
        variant_id: vid,
        product_id: Number(it?.product_id) || null,
        added_at:   it?.added_at || null,
        handle:     it?.handle   || null,
      });
    }

    // ゲスト側を追加（重複は added_at をサーバー優先で補完）
    for (const it of guestItems) {
      const vid = it.variant_id;

      if (merged.has(vid)) {
        // 重複あり：product_id / handle が欠損している側を補完するだけ
        const existing = merged.get(vid);
        if (!existing.product_id && it.product_id) existing.product_id = it.product_id;
        if (!existing.handle    && it.handle)     existing.handle     = it.handle;
        // added_at はサーバー優先なので上書きしない
      } else {
        // 新規追加
        merged.set(vid, { ...it });
      }
    }

    return Array.from(merged.values());
  }

  // ---- マージ後データをサーバーへ一括保存（App Proxy 経由）----
  // サーバー仕様: action="merge", キーは guest_items, レスポンス { status: "succeeded" }
  async function saveToServer(appProxyPath, guestItems) {
    const payload = {
      action:      'merge',
      guest_items: guestItems.map(it => ({
        variant_id: it.variant_id,
        product_id: it.product_id ?? null,
      })),
    };

    const res = await fetch(appProxyPath, {
      method:      'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept:         'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) throw new Error(`save to server failed: ${res.status}`);

    const data = await res.json();
    if (data?.status !== 'succeeded') {
      throw new Error(data?.message || 'server save returned non-succeeded status');
    }

    return data;
  }

  // ---- 引き継ぎ済みフラグ ----
  function hasMigrated(customerId) {
    const flag = safeRead(MIGRATED_FLAG_KEY);
    // customer ごとにフラグを管理（別アカウントでのログインに対応）
    return flag?.customerId === customerId;
  }

  function markMigrated(customerId) {
    safeWrite(MIGRATED_FLAG_KEY, { customerId, migratedAt: new Date().toISOString() });
  }

  // ---- ステータスUI表示（オプション）----
  function showMigrationStatus(msg, type = 'info') {
    // data-wishlist-migration-status 属性を持つ要素があれば表示
    const el = document.querySelector('[data-wishlist-migration-status]');
    if (!el) return;
    el.textContent = msg;
    el.dataset.wishlistMigrationStatus = type;   // 'info' | 'success' | 'error'
    el.hidden = false;
    if (type === 'success') {
      setTimeout(() => { el.hidden = true; }, 3000);
    }
  }

  // ---- メイン処理 ----
  async function runMigration() {
    const sync = getSyncConfig();

    // 前提条件チェック
    if (!sync.enabled || !sync.customerId || !sync.appProxyPath) return;
    if (hasMigrated(sync.customerId)) return;

    // ゲストデータ確認
    const guestRaw   = safeRead(GUEST_STORAGE_KEY);
    const guestItems = normalizeGuestItems(guestRaw);

    if (guestItems.length === 0) {
      // ゲストデータなし → フラグだけ立てて終了（次回以降スキップ）
      markMigrated(sync.customerId);
      return;
    }

    console.info(`[wishlist-migration] starting migration: ${guestItems.length} guest item(s)`);
    showMigrationStatus('お気に入りを引き継いでいます…', 'info');

    try {
      // サーバー側 merge action でマージ・保存を一括実行
      // （get → クライアントマージ → save の3段階は不要。サーバーが重複排除まで行う）
      const result = await saveToServer(sync.appProxyPath, guestItems);
      console.info('[wishlist-migration] merge result:', result?.merge_result);

      // 成功：ゲスト localStorage を削除 → フラグ記録
      safeRemove(GUEST_STORAGE_KEY);
      markMigrated(sync.customerId);

      console.info('[wishlist-migration] migration succeeded');
      showMigrationStatus('お気に入りを引き継ぎました', 'success');

      // ウィッシュリストページが開いていれば再描画
      const isWishlistPage = Boolean(document.querySelector('[data-wishlist-page]'));
      if (isWishlistPage) {
        window.location.reload();
      }

    } catch (err) {
      // 失敗：localStorage は保持（次回リトライ可能）
      console.warn('[wishlist-migration] migration failed:', err);
      showMigrationStatus('お気に入りの引き継ぎに失敗しました。再度お試しください。', 'error');
    }
  }

  // ---- エントリポイント ----
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runMigration);
  } else {
    runMigration();
  }

  // デバッグ用
  window.WishlistMigrationDebug = {
    run: runMigration,
    clearFlag() {
      safeRemove(MIGRATED_FLAG_KEY);
      console.info('[wishlist-migration] flag cleared');
    },
    getFlag() {
      return safeRead(MIGRATED_FLAG_KEY);
    },
  };
})();
